// src/FichaCheckin.js

import React from 'react';
import { Box, Paper, Typography, Grid, Divider, List, ListItem, ListItemText } from '@mui/material';

// Funções de formatação
const formatarData = (dataString) => {
  if (!dataString) return '--/--/----';
  const [ano, mes, dia] = dataString.split('-');
  return `${dia}/${mes}/${ano}`;
};

const formatarMoeda = (valor) => {
  return (valor || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
};

const traduzirTipoHospede = (tipo) => {
  switch (tipo) {
    case 'AABB_ARACAJU': return 'Associado AABB Aracaju';
    case 'OUTRA_AABB': return 'Associado Outra AABB';
    case 'NAO_ASSOCIADO': return 'Não Associado';
    default: return tipo || 'Não informado';
  }
};

const InfoItem = ({ label, value }) => (
  <Box sx={{ mb: 1.5 }}>
    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textTransform: 'uppercase', fontSize: '0.65rem' }}>{label}</Typography>
    <Typography variant="body1">{value || 'Não informado'}</Typography>
  </Box>
);

const FichaCheckin = ({ reserva, hospede, despesasExtras }) => {
  if (!reserva || !hospede) {
    return <Typography>Dados insuficientes para gerar a ficha.</Typography>;
  }

  const valorTotalBruto = reserva.valorTotal || 0;
  const totalDespesasExtras = (despesasExtras || []).reduce((acc, item) => acc + item.valor, 0);
  const valorTotalComExtras = valorTotalBruto + totalDespesasExtras;
  const desconto = reserva.desconto || 0;

  // --- CÁLCULO ATUALIZADO PARA INCLUIR O 2º ADIANTAMENTO ---
  const valorAdiantamento1 = reserva.adiantamento?.valor || 0;
  const valorAdiantamento2 = reserva.adiantamento2?.valor || 0;
  const totalAdiantamento = valorAdiantamento1 + valorAdiantamento2;
  const valorPagamentoFinal = reserva.pagamentoRestante?.valor || 0;
  const totalPago = totalAdiantamento + valorPagamentoFinal;
  const saldoDevedor = valorTotalComExtras - desconto - totalPago;

  return (
    <Paper sx={{ p: 4, color: 'black', backgroundColor: 'white' }}>
      <Typography variant="h5" align="center" gutterBottom>
        FICHA DE CHECK-IN / REGISTRO DE HÓSPEDE
      </Typography>
      <Divider sx={{ my: 2 }} />

      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Typography variant="h6" gutterBottom>Dados da Reserva</Typography>
          <Grid container spacing={2}>
            <Grid item xs={12}><InfoItem label="Voucher da Reserva" value={reserva.codigoVoucher} /></Grid>
            <Grid item xs={12} sm={3}><InfoItem label="Chalé" value={reserva.chaleNome} /></Grid>
            <Grid item xs={12} sm={3}><InfoItem label="Check-in" value={formatarData(reserva.dataCheckin)} /></Grid>
            <Grid item xs={12} sm={3}><InfoItem label="Check-out" value={formatarData(reserva.dataCheckout)} /></Grid>
            <Grid item xs={12} sm={3}><InfoItem label="Nº de Diárias" value={`${reserva.numDiarias} diária(s)`} /></Grid>
          </Grid>
        </Grid>
      </Grid>
      
      <Divider sx={{ my: 2 }} />

      <Typography variant="h6" gutterBottom>Hóspede Principal</Typography>
      <Grid container spacing={2}>
        <Grid item xs={6}><InfoItem label="Nome Completo" value={hospede.nome} /></Grid>
        <Grid item xs={3}><InfoItem label="CPF" value={hospede.cpf} /></Grid>
        <Grid item xs={3}><InfoItem label="Celular" value={hospede.celular} /></Grid>
        <Grid item xs={6}><InfoItem label="Email" value={hospede.email} /></Grid>
        <Grid item xs={6}><InfoItem label="Tipo" value={traduzirTipoHospede(hospede.tipoHospede)} /></Grid>
        
        {hospede.tipoHospede === 'OUTRA_AABB' && (
          <Grid item xs={6}><InfoItem label="AABB de Origem" value={hospede.qualAABB} /></Grid>
        )}

        {hospede.tipoHospede === 'NAO_ASSOCIADO' && (
          <Grid item xs={12}><InfoItem label="Indicado por" value={hospede.indicadoPor} /></Grid>
        )}
      </Grid>

      {reserva.acompanhantes && reserva.acompanhantes.filter(ac => ac.nome).length > 0 && (
        <>
            <Divider sx={{ my: 2 }} />
            <Typography variant="h6" gutterBottom>Acompanhantes</Typography>
            <List dense>
                {reserva.acompanhantes.map((ac, index) => ac.nome && (
                    <ListItem key={index} sx={{ py: 0 }}>
                        <ListItemText primary={ac.nome} secondary={`CPF: ${ac.cpf || 'Não informado'}`} />
                    </ListItem>
                ))}
            </List>
        </>
      )}

      <Divider sx={{ my: 2 }} />

      <Typography variant="h6" gutterBottom>Resumo Financeiro</Typography>
      {/* --- LAYOUT FINANCEIRO ATUALIZADO --- */}
      <Grid container spacing={2}>
        <Grid item xs={3}><InfoItem label="Valor Estadia" value={formatarMoeda(valorTotalBruto)} /></Grid>
        <Grid item xs={3}><InfoItem label="Despesas Extras" value={formatarMoeda(totalDespesasExtras)} /></Grid>
        <Grid item xs={3}><InfoItem label="Desconto" value={`- ${formatarMoeda(desconto)}`} /></Grid>
        <Grid item xs={3}><InfoItem label="Total Geral" value={formatarMoeda(valorTotalComExtras - desconto)} /></Grid>
      </Grid>
      <Divider sx={{ my: 1.5 }} />
      <Grid container spacing={2}>
        <Grid item xs={3}><InfoItem label="1º Adiantamento Pago" value={formatarMoeda(valorAdiantamento1)} /></Grid>
        {valorAdiantamento2 > 0 && 
            <Grid item xs={3}><InfoItem label="2º Adiantamento Pago" value={formatarMoeda(valorAdiantamento2)} /></Grid>
        }
        <Grid item xs={3}><InfoItem label="Pagamento Final" value={formatarMoeda(valorPagamentoFinal)} /></Grid>
        <Grid item xs={3}><InfoItem label="Total Pago" value={formatarMoeda(totalPago)} /></Grid>
      </Grid>
      <Divider sx={{ my: 1.5 }} />
      <Box sx={{ mt: 2, textAlign: 'right' }}>
        <Typography variant="h6" color="error.main">SALDO DEVEDOR NO CHECK-IN: {formatarMoeda(saldoDevedor)}</Typography>
      </Box>

      <Divider sx={{ my: 2 }} />

      <Box sx={{ mt: 3, p: 2, border: '1px solid #eee', borderRadius: 1, fontSize: '0.8rem' }}>
        <Typography variant="subtitle2" gutterBottom align="center">
            TERMO DE CONSENTIMENTO PARA TRATAMENTO DE DADOS PESSOAIS
        </Typography>
        <Typography variant="caption" component="p" align="justify">
            Ao assinar esta ficha de check-in, eu, <strong>{hospede.nome}</strong>, na qualidade de titular dos dados, autorizo de forma livre e inequívoca a <strong>Associação Atlética Banco do Brasil - AABB Aracaju</strong> a coletar, armazenar e utilizar os dados pessoais meus e dos meus acompanhantes, preenchidos neste documento. Declaro estar ciente de que o tratamento dos meus dados tem como finalidades principais o cumprimento das obrigações legais e contratuais decorrentes da prestação de serviços de hospedagem e o contato para fins de marketing, incluindo o envio de promoções, pacotes e novidades sobre futuras hospedagens através de e-mail, WhatsApp, SMS ou ligação. Os dados serão armazenados de forma segura e pelo período necessário para o cumprimento das finalidades aqui descritas e das obrigações legais vigentes.
        </Typography>
      </Box>

      <Box sx={{ mt: 6, textAlign: 'center' }}>
        <Divider sx={{ width: '60%', mx: 'auto' }} />
        <Typography variant="body1" sx={{ mt: 1 }}>{hospede.nome}</Typography>
        <Typography variant="caption" color="text.secondary">(Assinatura do Hóspede)</Typography>
      </Box>
    </Paper>
  );
};

export default FichaCheckin;

